import {
  POSICOES_VEICULO,
  ROTULO_POSICAO_VEICULO,
  mensagemFotosFaltantes,
  posicoesFaltantes,
  type PosicaoVeiculo,
} from "@shared/veiculo-fotos";
import type { LaudoWithDetails } from "@shared/schema";
import { comPrazoPdf } from "./laudo-pdf-prazo";

const TIMEOUT_FOTO_MS = 30_000;
const LADO_MAXIMO_PX = 1600;

type LaudoComFotos = Pick<LaudoWithDetails, "moduloDetranVeiculoFoto" | "veiculo">;

export interface FotoVeiculoPdf {
  rotulo: string;
  dataUrl: string;
}

/** A mensagem já está pronta para o usuário: vai direto ao aviso "Erro ao gerar PDF". `cause` guarda o detalhe técnico. */
export class FotosVeiculoPdfError extends Error {
  constructor(mensagem: string, cause?: unknown) {
    super(mensagem, { cause });
    this.name = "FotosVeiculoPdfError";
  }
}

/** Ordena as fotos existentes; a flag controla obrigatoriedade, não a presença no PDF novo. */
export function fotosVeiculoParaPdf(laudo: LaudoComFotos) {
  const fotos = laudo.veiculo?.fotos ?? [];
  return POSICOES_VEICULO.flatMap((posicao: PosicaoVeiculo) => {
    const url = fotos.find((x) => x.posicao === posicao)?.fotoUrl;
    return url ? [{ posicao, rotulo: ROTULO_POSICAO_VEICULO[posicao], url }] : [];
  });
}

/** Mesma proporção, lado maior até `ladoMaximo`, nunca amplia. */
export function dimensoesParaCaber(largura: number, altura: number, ladoMaximo = LADO_MAXIMO_PX) {
  const escala = Math.min(1, ladoMaximo / Math.max(largura, altura));
  return { largura: Math.max(1, Math.round(largura * escala)), altura: Math.max(1, Math.round(altura * escala)) };
}

/**
 * Só no navegador. Reencoda em JPEG com o lado maior em 1600 px: o PDF não incha com fotos de 12 MP
 * e WebP (aceito no upload, mas ilegível para o react-pdf) vira JPEG. Imagem que não decodifica rejeita.
 */
export async function reduzirFotoVeiculo(foto: Blob, ladoMaximo = LADO_MAXIMO_PX): Promise<string> {
  const canvas = document.createElement("canvas");
  const imagem = await createImageBitmap(foto, { imageOrientation: "from-image" });
  try {
    const { largura, altura } = dimensoesParaCaber(imagem.width, imagem.height, ladoMaximo);
    canvas.width = largura;
    canvas.height = altura;
    const contexto = canvas.getContext("2d");
    if (!contexto) throw new Error("Canvas indisponível.");
    contexto.fillStyle = "#fff"; // PNG com transparência não pode virar fundo preto no JPEG
    contexto.fillRect(0, 0, largura, altura);
    contexto.drawImage(imagem, 0, 0, largura, altura);
    const jpeg = canvas.toDataURL("image/jpeg", 0.85);
    // Canvas grande demais ou sem memória (Safari/iOS) devolve "data:,"; sem suporte ao tipo o navegador cai para PNG.
    // Nenhum dos dois é a foto reduzida: é falha da foto, nunca uma imagem vazia no PDF.
    if (!jpeg.startsWith("data:image/jpeg;base64,")) throw new Error("O navegador não conseguiu gerar a foto reduzida.");
    return jpeg;
  } finally {
    canvas.width = canvas.height = 0; // devolve a memória do canvas já: o Safari/iOS a segura até o coletor rodar
    imagem.close();
  }
}

/**
 * M1 (T19): só baixa o que está no armazenamento do próprio sistema. O endereço da foto vem do banco e, em laudos antigos, de
 * colunas que o usuário alcançava pelo REST; sem esta checagem uma URL externa (ou interna) entraria no PDF. Mesmo esquema, host
 * e porta, sem usuário/senha, e o caminho (já normalizado pelo URL: "..", "//") começa pelo da origem. `origem` termina em "/".
 */
export function urlNaOrigem(url: string, origem: string): boolean {
  try {
    const u = new URL(url);
    const o = new URL(origem);
    return u.protocol === o.protocol && u.host === o.host && !u.username && !u.password && u.pathname.startsWith(o.pathname);
  } catch {
    return false;
  }
}

async function baixar(url: string, timeoutMs: number): Promise<Blob> {
  const resposta = await fetch(url, { signal: AbortSignal.timeout(timeoutMs) });
  if (!resposta.ok) throw new Error(`HTTP ${resposta.status}`);
  return resposta.blob(); // o mesmo sinal também cobre a leitura do corpo
}

/**
 * Fotos do veículo para a página "REGISTROS FOTOGRÁFICOS DO VEÍCULO". Num laudo do módulo as 4 são
 * evidência obrigatória: faltar registro, não baixar (HTTP, rede ou timeout) ou não decodificar FALHA
 * a geração com FotosVeiculoPdfError. Nunca pula foto cadastrada. Sem módulo e sem fotos, não toca na rede.
 */
export async function carregarFotosVeiculoParaPdf(
  laudo: LaudoComFotos,
  { origemPermitida, timeoutMs = TIMEOUT_FOTO_MS, reduzir = reduzirFotoVeiculo }:
    { origemPermitida: string; timeoutMs?: number; reduzir?: (foto: Blob) => Promise<string> },
): Promise<FotoVeiculoPdf[]> {
  const fotos = fotosVeiculoParaPdf(laudo);
  if (!laudo.moduloDetranVeiculoFoto && fotos.length === 0) return [];
  const faltam = posicoesFaltantes(fotos);
  if (laudo.moduloDetranVeiculoFoto && faltam.length) {
    throw new FotosVeiculoPdfError(
      `${mensagemFotosFaltantes(faltam)} O laudo do módulo Detran só gera o PDF com as 4 fotos do veículo: envie as que faltam e tente novamente.`,
    );
  }

  const foraDaOrigem = fotos.filter((foto) => !urlNaOrigem(foto.url, origemPermitida));
  if (foraDaOrigem.length) {
    throw new FotosVeiculoPdfError(
      `A foto do veículo (${foraDaOrigem.map((f) => f.rotulo).join(", ")}) não está no armazenamento do sistema e não pode entrar no laudo. ` +
        (laudo.moduloDetranVeiculoFoto
          ? "O laudo do módulo Detran só gera o PDF com as 4 fotos enviadas pelo sistema: envie a foto novamente e tente gerar o PDF."
          : "Envie a foto pelo sistema novamente e tente gerar o PDF."),
    );
  }

  // Downloads juntos (cada um com o seu timeout); a redução é uma por vez, pois uma foto decodificada ocupa dezenas de MB.
  const baixadas = await Promise.allSettled(fotos.map((foto) => baixar(foto.url, timeoutMs)));
  const prontas: FotoVeiculoPdf[] = [];
  const falhas: { rotulo: string; causa: unknown }[] = [];
  for (let i = 0; i < fotos.length; i++) {
    const foto = fotos[i];
    const baixada = baixadas[i];
    try {
      if (baixada.status === "rejected") throw baixada.reason;
      prontas.push({ rotulo: foto.rotulo, dataUrl: await comPrazoPdf(reduzir(baixada.value), timeoutMs, "A decodificação da foto excedeu o tempo limite.") });
    } catch (causa) {
      falhas.push({ rotulo: foto.rotulo, causa });
    }
  }
  if (falhas.length) {
    throw new FotosVeiculoPdfError(
      `Não foi possível obter todas as fotos do veículo (${falhas.map((f) => f.rotulo).join(", ")}). ` +
        (laudo.moduloDetranVeiculoFoto ? "O laudo do módulo Detran não pode ser gerado sem as 4 fotos. " : "O PDF não pode omitir fotos cadastradas. ") +
        "Verifique a conexão e tente gerar o PDF novamente.",
      falhas,
    );
  }
  return prontas;
}
